import { StrictMode, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './shell/auth';
import { useLocation } from './shell/navigation';
import { PaymentResultBanner } from './components/PaymentResultBanner';
import './styles.css';
import './i18n';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

// ── ленивые секции (бандлы грузятся по требованию) ──
const Landing = lazy(() => import('./pages/Landing/index'));
const Offer = lazy(() => import('./pages/Legal/Offer'));
const Terms = lazy(() => import('./pages/Legal/Terms'));
const Refund = lazy(() => import('./pages/Legal/Refund'));
const AUP = lazy(() => import('./pages/Legal/AUP'));
const Privacy = lazy(() => import('./pages/Legal/Privacy'));
const PersonalData = lazy(() => import('./pages/Legal/PersonalData'));
const Security = lazy(() => import('./pages/Legal/Security'));
const Cookies = lazy(() => import('./pages/Legal/Cookies'));
const Login = lazy(() => import('./pages/Login'));
const TelegramLogin = lazy(() => import('./pages/Login/TelegramLogin'));
const AuthVerify = lazy(() => import('./pages/Auth/Verify'));
const AuthForgot = lazy(() => import('./pages/Auth/Forgot'));
const AuthReset = lazy(() => import('./pages/Auth/Reset'));
const ClassicApp = lazy(() => import('./App').then((m) => ({ default: m.App })));
const SkillShare = lazy(() => import('./pages/SkillShare'));
const PublicCanvas = lazy(() => import('./pages/PublicCanvas'));

type Section = 'public' | 'classic';

function sectionFor(path: string): Section {
  if (
    path.startsWith('/legal/') || path === '/login' || path === '/login/telegram' ||
    path.startsWith('/auth/') || path.startsWith('/s/') ||
    path.startsWith('/public/canvas/')
  ) return 'public';
  // Корень «/» (и /index.html), /app, /web, /projects, /canvas — единая оболочка App:
  // «лендинг = само приложение» (гость видит весь шелл, auth-гейт на действии).
  // Старый маркетинг-лендинг остаётся статикой на /lp/v2-noir.html.
  return 'classic';
}

const fallback = <div style={{ color: 'var(--text, #fff)', padding: 24 }}>…</div>;

function PublicPage({ path }: { path: string }) {
  if (path.startsWith('/legal/offer')) return <Offer />;
  if (path.startsWith('/legal/terms')) return <Terms />;
  if (path.startsWith('/legal/refund')) return <Refund />;
  if (path.startsWith('/legal/aup')) return <AUP />;
  if (path.startsWith('/legal/privacy')) return <Privacy />;
  if (path.startsWith('/legal/personal-data')) return <PersonalData />;
  if (path.startsWith('/legal/security')) return <Security />;
  if (path.startsWith('/legal/cookies')) return <Cookies />;
  if (path === '/login/telegram') return <TelegramLogin />;
  if (path === '/login') return <Login />;
  if (path.startsWith('/s/')) return <SkillShare />;
  if (path.startsWith('/public/canvas/')) return <PublicCanvas />;
  if (path.startsWith('/auth/verify')) return <AuthVerify />;
  if (path.startsWith('/auth/forgot')) return <AuthForgot />;
  if (path.startsWith('/auth/reset')) return <AuthReset />;
  return <Landing />;
}

// Public-роуты помечаем html.pm-public, чтобы styles.css снял глобальный
// overflow:hidden (нужен Mini App / канвасу, но ломает скролл лендинга).
function isPublic(path: string): boolean {
  return sectionFor(path) === 'public';
}
if (isPublic(window.location.pathname)) {
  document.documentElement.classList.add('pm-public');
} else {
  // Mini App: блокируем pinch- и auto-zoom. iOS зумит экран при фокусе на любой
  // input/textarea с font-size < 16px — отсюда «прыжки» и зум интерфейса в TG WebApp.
  // Лендинг (public) оставляем масштабируемым для доступности.
  const vp = document.querySelector('meta[name="viewport"]');
  if (vp) {
    vp.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    );
  }
  // iOS: помечаем html.pm-ios, чтобы оболочка тянулась за живым --tg-viewport-height
  // (SDK обновляет его на viewportChanged) — композер поднимается над клавиатурой,
  // а не «прыгает». Скоупим строго на iOS, чтобы не задеть Android/desktop/web.
  try {
    if (window.Telegram?.WebApp?.platform === 'ios') {
      document.documentElement.classList.add('pm-ios');
    }
  } catch { /* ignore */ }
}

function Root() {
  const path = useLocation();
  const section = sectionFor(path);

  useEffect(() => {
    document.documentElement.classList.toggle('pm-public', section === 'public');
  }, [section]);

  // Публичное — без auth-провайдера (лендинг не должен поллить initData/редиректить).
  if (section === 'public') {
    return (
      <Suspense fallback={fallback}>
        <PublicPage path={path} />
        <PaymentResultBanner />
      </Suspense>
    );
  }

  // Приложение: Чат / История / Библиотека / Студия / Настройки — единая
  // оболочка под общей сессией. Студия (канвас) — вкладка внутри App.
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Suspense fallback={fallback}>
          <ClassicApp />
        </Suspense>
        <PaymentResultBanner />
      </AuthProvider>
    </QueryClientProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
